import { useState } from "react";
import { X } from "lucide-react";
import type { TemplateRunBody } from "../../../api/types";
import { cn, Field, TextInput } from "../../../components/ui";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function DateChips({ label, dates, onChange, testId }: { label: string; dates: string[]; onChange: (d: string[]) => void; testId: string }) {
  const [v, setV] = useState("");
  const add = () => { if (v && !dates.includes(v)) onChange([...dates, v].sort()); setV(""); };
  return (
    <div data-testid={testId}>
      <p className="label-caps mb-2 text-zinc-400">{label}</p>
      <div className="flex gap-2">
        <TextInput type="date" value={v} onChange={(e) => setV(e.target.value)} aria-label={label} data-testid={`${testId}-input`} />
        <button type="button" onClick={add} className="rounded-sm border border-white/15 px-4 text-xs uppercase tracking-wider text-zinc-200 hover:border-signal" data-testid={`${testId}-add`}>Add</button>
      </div>
      <div className="mt-2 flex flex-wrap gap-2">
        {dates.map((d) => (
          <span key={d} className="inline-flex items-center gap-1 rounded-full border border-white/15 px-3 py-1 font-mono text-xs text-zinc-200" data-testid={`${testId}-chip-${d}`}>
            {d}<button type="button" aria-label={`Remove ${d}`} onClick={() => onChange(dates.filter((x) => x !== d))}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
    </div>
  );
}

export function RecurringFields({ body, set }: { body: TemplateRunBody; set: (p: Partial<TemplateRunBody>) => void }) {
  const wd = body.weekdays ?? [];
  return (
    <div className="space-y-4">
      <div>
        <p className="label-caps mb-2 text-zinc-400">Days of the week</p>
        <div className="flex flex-wrap gap-2" role="group" aria-label="Days of the week">
          {DAYS.map((d, i) => (
            <button key={d} type="button" aria-pressed={wd.includes(i)} onClick={() => set({ weekdays: wd.includes(i) ? wd.filter((x) => x !== i) : [...wd, i].sort() })}
              className={cn("h-9 rounded-full border px-4 text-xs uppercase tracking-wider transition-colors", wd.includes(i) ? "border-signal bg-signal/15 text-white" : "border-white/15 text-zinc-400 hover:text-white")}
              data-testid={`run-weekday-${i}`}>{d}</button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <Field label="Start date" htmlFor="run-start"><TextInput id="run-start" type="date" value={body.start_date ?? ""} onChange={(e) => set({ start_date: e.target.value })} data-testid="run-start-date" /></Field>
        <Field label="End date (required)" htmlFor="run-end"><TextInput id="run-end" type="date" value={body.end_date ?? ""} onChange={(e) => set({ end_date: e.target.value })} data-testid="run-end-date" /></Field>
      </div>
      <p className="text-[11px] text-zinc-500">Up to 6 months and 200 shifts per run.</p>
      <DateChips label="Skip dates (optional)" dates={body.skip_dates ?? []} onChange={(d) => set({ skip_dates: d })} testId="run-skip-dates" />
    </div>
  );
}
